/**
 * DECISIONS 201 (8), the owner, Oct 19: "now medic and engineer are sort of worthwhile, once we have enough tasks we can add
 * specialty roles". The medic's and the engineer's looks on the soldier (B, helmet C, the plate carrier). From three
 * options each (design_sheets/roles.html) he picked, Oct 20: "Medic A and Engineer A, but I think we'll give Engineer A
 * some more stuff, but probably actual game useful stuff".
 * - **The medic** — a green band with the white cross on each upper arm, a medical rucksack in the pack's place (the cross on
 *   its back and its top, for the stops above), crosses on the helmet's sides. Every medic mark is the first-aid sign, a
 *   white cross on green (ISO 7010): never the red cross on white, the red crescent or the red crystal, which are emblems
 *   protected under the Geneva Conventions.
 * - **The engineer** — the sapper (A): a demolition satchel at the support hip (a hazard band on it), the folded
 *   entrenching tool on the pack's back (66.8's digs), a coil of det cord on the pack. And the game's kit (`kit`, on unless
 *   the sheet shows A as he picked it): the build kit for relays, FOBs and modules (69.4's kits: the mast sections and the
 *   stake bag strapped up the pack's strong side), the repair wrench for vehicles and structures (on the cummerbund), the
 *   breaching charges for modules (63.14's breach: a strip charge up the pack's support side) and the pry bar for rubble
 *   (201 (6): engineers clear wreckage; across the back, its claw over the support shoulder).
 * Every piece goes on a group that already moves, so it bakes into that group's draw (no new draws); the far tier gets a
 * few boxes (`farRole`). The colours are the role's, not the team's: the first-aid green and white, the steel and the
 * wood, the hazard yellow — they read on any team. Person units, the model's frame (+z front, +x the strong side as built).
 * - **The support** (69.45, DECISIONS 268 (3): the sheet's A, design_sheets/specialists.html) — the ammunition case on a pack
 *   frame in the pack's place (as the medic's rucksack), a smaller can on top, the straps over the shoulders; the case's
 *   yellow band and cream stencil. `ammoCaseModel` is the same case set down open on the ground (the `ammoCase` structure).
 * - **The crewman** (69.46, DECISIONS 269 (3): the sheet's look A) — the padded crew helmet in the line helmet's place (its ear
 *   cups, the boom mic, goggles pushed up on the shell's front), a tool roll at the strong hip; with no plates issued, no
 *   plate carrier and no pack — the tank's man from afar, his lighter kit the trade-off (soldierLooks.ts `crewLookA`).
 * Rendering only: PlayerState.role says who wears it (the support's case is the sim's with GameConfig.supportRole; the
 * crewman's seat work with GameConfig.crewmanRole, core/crewman.ts).
 */
import * as THREE from 'three';
import { CC, mix, loft, cbox, bar, mat } from './soldierKit';
import { HELMET_FIT } from './helmetLooks';
import type { PersonModel } from './personModel';
import type { UnitPalette } from './unitPalette';
import type { HelmetKind } from './armourLooks';
import type { UnitRole } from '../types';

/** What the role wears: `kit` the engineer's game kit over A (absent: on; false: A as the owner picked it, the sheet's before). */
export interface RoleDress { role: UnitRole; kit?: boolean }

/** The role's colours. */
export const ROLE_COLOURS = {
  aid: '#1f9e50', aidCross: '#f2f0ea',   // the first-aid sign: a white cross on green (not the protected red cross)
  canvas: '#6a6545', canvasDark: '#4f4b33', strap: '#3a372a',
  steel: '#8b8f93', darkSteel: '#4a4d50', wood: '#7a5a3a', hazard: '#d6a51c', cord: '#c76a2a',
  can: '#5b6140', canDark: '#3f442c', stencil: '#e4dac0', mag: '#2e2a26',   // 69.45: the support's case (its band the hazard yellow)
  shell: '#7a7652', pad: '#4f4b33', cup: '#2a2a2e', goggle: '#9cc8e0', goggleRim: '#1c1d20',   // 69.46: the crewman's padded helmet
};
const C = ROLE_COLOURS;

/** Does the role carry its own load on the back in the pack's place (the carrier then leaves its pack off)? */
export function roleReplacesPack(r: RoleDress | undefined): boolean {
  return r?.role === 'medic' || r?.role === 'support';
}

// ---- the pieces ---------------------------------------------------------------------------------------------------------

/**
 * The first-aid sign, a white cross on a green square, flat on a face: `n` the face's outward axis ('x' | 'y' | 'z') and its sign, `s` the
 * square's side. The cross's bars are a quarter of it wide.
 */
function crossPanel(parent: THREE.Object3D, s: number, x: number, y: number, z: number, n: 'x' | 'y' | 'z', sign: 1 | -1, ground = true): void {
  const t = 0.008, arm = s * 0.74, w = s * 0.25, lift = sign * (t * 0.5 + 0.0025);
  const at = (o: THREE.Object3D) => { if (n === 'x') o.position.x += lift; else if (n === 'y') o.position.y += lift; else o.position.z += lift; };
  if (n === 'x') { if (ground) cbox(parent, t, s, s, C.aid, x, y, z); at(cbox(parent, t, arm, w, C.aidCross, x, y, z)); at(cbox(parent, t, w, arm, C.aidCross, x, y, z)); }
  else if (n === 'y') { if (ground) cbox(parent, s, t, s, C.aid, x, y, z); at(cbox(parent, arm, t, w, C.aidCross, x, y, z)); at(cbox(parent, w, t, arm, C.aidCross, x, y, z)); }
  else { if (ground) cbox(parent, s, s, t, C.aid, x, y, z); at(cbox(parent, arm, w, t, C.aidCross, x, y, z)); at(cbox(parent, w, arm, t, C.aidCross, x, y, z)); }
}

/** A green band with the white cross round the upper arm (over the shirt and the team's flash band: `buildArmParts`'s 0.087 at −0.1…−0.155). */
function armband(arm: { group: THREE.Group }): void {
  const out = Math.sign(arm.group.position.x);
  loft(arm.group, [{ y: -0.08, rx: 0.092, rz: 0.096 }, { y: -0.175, rx: 0.088, rz: 0.093 }], 6, C.aid, false, false);
  crossPanel(arm.group, 0.07, out * 0.094, -0.128, 0, 'x', out as 1 | -1, false);
}

/** A strap across the chest from the `sx` shoulder down to the other hip, lying on the carrier's front (two runs), and behind. */
function crossStrap(t: THREE.Group, frontAt: (y: number) => number, sx: number): void {
  const a: [number, number, number] = [sx * 0.17, 0.6, frontAt(0.6) + 0.006], b: [number, number, number] = [sx * 0.01, 0.36, frontAt(0.36) + 0.012], c: [number, number, number] = [-sx * 0.2, 0.08, frontAt(0.16) + 0.006];
  bar(t, a, b, 0.04, C.strap); bar(t, b, c, 0.04, C.strap);
  bar(t, [sx * 0.17, 0.6, -0.17], [-sx * 0.2, 0.1, -0.165], 0.04, C.strap);
}

/** A cross patch on each side of the helmet's shell. */
function helmetSideCrosses(h: THREE.Group, heavy: boolean): void {
  const f = HELMET_FIT[heavy ? 'heavy' : 'light'], e = 0.5, y = f.hy + f.ry * Math.sin(e), x = f.rx * Math.cos(e) + 0.006;
  for (const sx of [-1, 1] as const) {
    const g = new THREE.Group(); g.position.set(sx * x, y, f.hz); g.rotation.z = sx * e; h.add(g);   // tilted with the shell's side
    crossPanel(g, 0.075, 0, 0, 0, 'x', sx);
  }
}

// ---- the roles ----------------------------------------------------------------------------------------------------------

function medic(m: PersonModel, p: UnitPalette, heavyHelmet: boolean, helmeted: boolean): void {
  const t = m.torso;
  // the aid rucksack in the pack's place: a size bigger, body-coloured, the cross on its back and its top
  cbox(t, 0.34, 0.42, 0.2, p.body, 0, 0.33, -0.26, CC);
  cbox(t, 0.35, 0.08, 0.215, mix(p.body, p.dark, 0.45), 0, 0.53, -0.26);
  for (const sx of [-1, 1]) cbox(t, 0.05, 0.22, 0.13, mix(p.body, p.dark, 0.3), sx * 0.19, 0.27, -0.26, 0.006);   // side pockets
  crossPanel(t, 0.21, 0, 0.33, -0.36, 'z', -1);
  crossPanel(t, 0.15, 0, 0.57, -0.26, 'y', 1);
  armband(m.armL); armband(m.armR);
  if (helmeted) helmetSideCrosses(m.head, heavyHelmet);
}

/**
 * 69.45: the support's ammunition case (person units; its foot at y 0, the long side along z): a steel case with its band and
 * stencil plate, the lid shut with its handle — or `open`, the lid up behind it and magazines standing inside.
 */
function caseOn(p: THREE.Object3D, x: number, y: number, z: number, w: number, h: number, d: number, open = false): void {
  cbox(p, w, h, d, C.can, x, y + h / 2, z, 0.006);
  cbox(p, w + 0.01, 0.03, d + 0.01, C.hazard, x, y + h * 0.72, z);
  if (w > d) cbox(p, w * 0.4, h * 0.3, 0.005, C.stencil, x, y + h * 0.4, z - d / 2 - 0.003);   // the stencil plate on a long face (the back's, on the pack)
  else cbox(p, 0.005, h * 0.3, d * 0.4, C.stencil, x + w / 2 + 0.003, y + h * 0.4, z);
  if (open) {
    const lid = cbox(p, w + 0.02, 0.025, d + 0.02, C.canDark, x - w / 2 - 0.01, y + h + 0.1, z); lid.rotation.z = 1.25;
    for (let i = 0; i < 5; i++) cbox(p, 0.08, 0.12, 0.03, C.mag, x, y + h + 0.02, z - d / 2 + 0.05 + (i * (d - 0.1)) / 4);
  } else {
    cbox(p, w + 0.02, 0.03, d + 0.02, C.canDark, x, y + h + 0.015, z);
    bar(p, [x, y + h + 0.03, z - d * 0.2], [x, y + h + 0.06, z + d * 0.2], 0.016, C.darkSteel);   // the handle
  }
}

/** 69.45 (the sheet's A): the case on a pack frame in the pack's place, a smaller can on top, the straps over the shoulders. */
function support(m: PersonModel): void {
  const t = m.torso;
  for (const sx of [-1, 1]) bar(t, [sx * 0.15, 0.07, -0.2], [sx * 0.15, 0.57, -0.2], 0.04, C.darkSteel);   // the frame
  caseOn(t, 0, 0.12, -0.33, 0.34, 0.3, 0.2);
  caseOn(t, 0, 0.44, -0.31, 0.3, 0.16, 0.16);
  for (const sx of [-1, 1]) bar(t, [sx * 0.15, 0.6, -0.2], [sx * 0.15, 0.62, 0.12], 0.04, C.strap);
}

/** 69.45: the support's case set down open on the ground (the `ammoCase` structure; AMMO_CASE_MODEL_SIZE): its foot at y 0. */
export function ammoCaseModel(): THREE.Group {
  const g = new THREE.Group();
  caseOn(g, 0, 0, 0, 0.22, 0.28, 0.36, true);
  return g;
}

function engineer(m: PersonModel, frontAt: (y: number) => number, kit: boolean): void {
  const t = m.torso, h = m.hips;
  // the demolition satchel at the support hip (the holster is at the strong one), its hazard band, its strap from the strong shoulder
  cbox(h, 0.12, 0.2, 0.27, C.canvas, -0.3, -0.05, -0.02, CC);
  cbox(h, 0.128, 0.065, 0.275, C.canvasDark, -0.3, 0.04, -0.02);
  cbox(h, 0.008, 0.045, 0.24, C.hazard, -0.364, -0.07, -0.02);
  crossStrap(t, frontAt, 1);
  // the entrenching tool in its cover on the pack's back, its handle hanging below the pack
  cbox(t, 0.17, 0.2, 0.035, C.canvasDark, 0, 0.36, -0.352, 0.006);
  bar(t, [0, 0.27, -0.356], [0, -0.02, -0.356], 0.034, C.wood);
  cbox(t, 0.05, 0.05, 0.04, C.darkSteel, 0, 0.26, -0.356);
  // the det cord coiled on the pack's top
  const coil = loft(t, [{ y: -0.05, rx: 0.07, rz: 0.07 }, { y: 0.05, rx: 0.07, rz: 0.07 }], 8, C.cord, true, true);
  coil.rotation.z = Math.PI / 2; coil.position.set(0, 0.575, -0.245);
  if (!kit) return;
  // the build kit (69.4): the relay's mast sections and the stake bag, strapped up the pack's strong side, standing over the shoulder
  for (const [dx, dz] of [[0, 0], [0.032, 0.02], [0.032, -0.02]]) bar(t, [0.19 + dx, 0.14, -0.25 + dz], [0.19 + dx, 0.84, -0.25 + dz], 0.026, C.steel);
  cbox(t, 0.075, 0.2, 0.09, C.canvasDark, 0.205, 0.26, -0.25, 0.006);   // the stake bag
  for (const y of [0.45, 0.66]) cbox(t, 0.1, 0.025, 0.1, C.strap, 0.205, y, -0.25);
  cbox(t, 0.06, 0.03, 0.06, C.hazard, 0.206, 0.85, -0.25);   // the sections' caps, the kit's yellow
  // the breaching charges (63.14's breach): a strip charge up the pack's support side, its hazard bands
  cbox(t, 0.03, 0.44, 0.08, C.canvas, -0.18, 0.37, -0.25, 0.006);
  for (const y of [0.22, 0.37, 0.52]) cbox(t, 0.036, 0.03, 0.086, C.hazard, -0.18, y, -0.25);
  // the pry bar for rubble, across the back over everything, its claw over the support shoulder
  const top: [number, number, number] = [-0.24, 0.86, -0.39], end: [number, number, number] = [0.17, 0.02, -0.39];
  bar(t, top, end, 0.03, C.darkSteel);
  const claw = cbox(t, 0.1, 0.03, 0.035, C.darkSteel, top[0] - 0.035, top[1] + 0.01, top[2]); claw.rotation.z = -0.5;
  // the repair wrench for vehicles and structures, in a loop on the cummerbund's strong side (above the holster)
  bar(t, [0.235, 0.12, 0.04], [0.235, 0.33, 0.06], 0.03, C.steel);
  cbox(t, 0.035, 0.05, 0.085, C.steel, 0.235, 0.355, 0.06);
  cbox(t, 0.04, 0.03, 0.05, C.strap, 0.236, 0.2, 0.05);   // the loop
}

/** A cylinder `len` long round `axis`. */
function cyl(p: THREE.Object3D, r: number, len: number, c: string, x: number, y: number, z: number, axis: 'x' | 'y' | 'z', seg: number): THREE.Mesh {
  const m = new THREE.Mesh(new THREE.CylinderGeometry(r, r, len, seg), mat(c));
  if (axis === 'x') m.rotation.z = Math.PI / 2; else if (axis === 'z') m.rotation.x = Math.PI / 2;
  m.position.set(x, y, z); p.add(m); return m;
}

/** 69.46 (the sheet's look A): the crewman's padded helmet in the line helmet's place — the shell and its padded brim and ribs, the ear cups, the boom mic, the goggles pushed up on the front. */
export function crewHelmet(m: PersonModel): void {
  const h = new THREE.Group(); h.position.y = 0.045; m.head.add(h);   // (worn as high as the line helmet: the face under it)
  const shell = new THREE.Mesh(new THREE.SphereGeometry(0.15, 16, 9, 0, Math.PI * 2, 0, Math.PI * 0.62), mat(C.shell));
  shell.position.set(0, 0.11, -0.01); shell.scale.set(1.08, 1.02, 1.15); h.add(shell);
  const brim = new THREE.Mesh(new THREE.CylinderGeometry(0.168, 0.172, 0.05, 18, 1, true), mat(C.pad)); brim.position.set(0, 0.125, -0.01); brim.scale.set(1.0, 1, 1.08); h.add(brim);
  for (const sx of [-1, 1]) bar(h, [sx * 0.06, 0.15, 0.15], [sx * 0.05, 0.27, -0.02], 0.03, C.pad);   // the padded ribs
  for (const sx of [-1, 1]) { cyl(h, 0.07, 0.05, C.cup, sx * 0.15, 0.06, 0, 'x', 14); cyl(h, 0.05, 0.052, C.pad, sx * 0.152, 0.06, 0, 'x', 12); }   // the ear cups
  bar(h, [0.16, 0.04, 0.03], [0.07, -0.02, 0.15], 0.014, C.cup); cbox(h, 0.035, 0.025, 0.03, C.cup, 0.06, -0.025, 0.155);   // the boom mic
  const gog = new THREE.Group(); gog.position.set(0, 0.215, 0.135); gog.rotation.x = -0.75; h.add(gog);
  for (const sx of [-1, 1]) { cyl(gog, 0.042, 0.03, C.goggleRim, sx * 0.05, 0, 0, 'z', 12); cyl(gog, 0.033, 0.032, C.goggle, sx * 0.05, 0, 0.002, 'z', 12); }
  cbox(h, 0.32, 0.03, 0.27, C.goggleRim, 0, 0.18, 0);   // the goggles' strap round the shell
}

/** 69.46: the crewman's tool roll at the strong hip (for the track and the engine). */
function crewman(m: PersonModel): void {
  cyl(m.hips, 0.05, 0.26, C.canvasDark, 0.25, -0.05, -0.08, 'z', 8);
  cbox(m.hips, 0.012, 0.11, 0.03, C.strap, 0.25, -0.05, -0.02);
}

/** Dress the role on the soldier (soldierLooks.ts `buildSoldier` calls it, after the carrier): `frontAt` the carrier's front face at a height. */
export function dressRole(m: PersonModel, p: UnitPalette, r: RoleDress, helmet: HelmetKind, frontAt: (y: number) => number): void {
  if (r.role === 'medic') medic(m, p, helmet === 'heavy', helmet !== 'none');
  else if (r.role === 'support') support(m);
  else if (r.role === 'crewman') crewman(m);
  else engineer(m, frontAt, r.kit !== false);
}

// ---- the far tier -------------------------------------------------------------------------------------------------------

/** One box of the far figure (person units, the standing figure's frame; `bar` a box from a to b). */
export type FarPart = { box: [w: number, h: number, d: number, x: number, y: number, z: number]; c: string } | { bar: [[number, number, number], [number, number, number], number]; c: string };

/**
 * The role at the far tier: a few boxes over the standing upper body (the pack's at z −0.22, its back −0.29, the helmet's
 * top 1.78), and the prone figure's (the pack's top at y 0.39, the head ahead at +z). `pack: false` leaves the figure's
 * pack off (the rucksack is in its place).
 */
export function farRole(r: RoleDress, p: UnitPalette): { upper: FarPart[]; prone: FarPart[]; pack: boolean } {
  const B = (w: number, h: number, d: number, x: number, y: number, z: number, c: string): FarPart => ({ box: [w, h, d, x, y, z], c });
  const L = (a: [number, number, number], b: [number, number, number], t: number, c: string): FarPart => ({ bar: [a, b, t], c });
  if (r.role === 'medic') return {
    pack: false,
    upper: [B(0.36, 0.42, 0.2, 0, 1.24, -0.24, p.body), B(0.22, 0.22, 0.02, 0, 1.25, -0.35, C.aid), B(0.16, 0.05, 0.02, 0, 1.25, -0.365, C.aidCross), B(0.05, 0.16, 0.02, 0, 1.25, -0.365, C.aidCross),
      B(0.14, 0.09, 0.14, -0.31, 1.32, 0.02, C.aid), B(0.14, 0.09, 0.14, 0.31, 1.32, 0.02, C.aid)],
    prone: [B(0.36, 0.16, 0.42, 0, 0.36, 0.2, p.body), B(0.22, 0.02, 0.22, 0, 0.45, 0.2, C.aid), B(0.16, 0.02, 0.05, 0, 0.465, 0.2, C.aidCross), B(0.05, 0.02, 0.16, 0, 0.465, 0.2, C.aidCross)],
  };
  if (r.role === 'support') return {
    pack: false,
    upper: [B(0.34, 0.3, 0.2, 0, 1.1, -0.33, C.can), B(0.35, 0.03, 0.21, 0, 1.2, -0.33, C.hazard), B(0.3, 0.16, 0.16, 0, 1.36, -0.31, C.can)],
    prone: [B(0.34, 0.2, 0.3, 0, 0.42, 0.2, C.can), B(0.35, 0.21, 0.03, 0, 0.42, 0.2, C.hazard)],
  };
  if (r.role === 'crewman') return {
    pack: false,   // (look A: no carrier and no pack, as soldierLooks.ts crewLookA draws him with no plates issued)
    upper: [B(0.06, 0.12, 0.12, -0.17, 1.62, 0.01, C.cup), B(0.06, 0.12, 0.12, 0.17, 1.62, 0.01, C.cup), B(0.12, 0.1, 0.26, 0.29, 0.86, -0.08, C.canvasDark)],
    prone: [B(0.12, 0.26, 0.1, 0.29, 0.3, -0.3, C.canvasDark)],
  };
  const upper = [B(0.13, 0.2, 0.27, -0.31, 0.88, -0.02, C.canvas), B(0.02, 0.05, 0.24, -0.38, 0.86, -0.02, C.hazard), B(0.17, 0.2, 0.04, 0, 1.28, -0.31, C.canvasDark), L([0, 1.18, -0.31], [0, 0.9, -0.31], 0.04, C.wood), B(0.14, 0.1, 0.1, 0, 1.44, -0.22, C.cord)];
  const prone = [B(0.17, 0.04, 0.2, 0, 0.41, 0.15, C.canvasDark), B(0.14, 0.1, 0.1, 0, 0.44, 0.34, C.cord)];
  if (r.kit !== false) {
    upper.push(B(0.09, 0.72, 0.09, 0.2, 1.42, -0.23, C.steel), B(0.1, 0.05, 0.1, 0.2, 1.8, -0.23, C.hazard), L([-0.24, 1.8, -0.35], [0.17, 0.94, -0.35], 0.05, C.darkSteel));
    prone.push(B(0.09, 0.09, 0.72, 0.2, 0.37, 0.25, C.steel), L([-0.24, 0.43, 0.62], [0.17, 0.43, -0.3], 0.05, C.darkSteel));
  }
  return { pack: true, upper, prone };
}
